<script setup>
import { computed, inject } from 'vue'
import { useActionCenterStore } from '../../stores/ActionCenterStore'
import { useNavigationStore } from '../../stores/NavigationStore'
import { useTemplateVariablesStore } from '../../stores/TemplateVariablesStore'
import { storeToRefs } from 'pinia'
import { extractActiveEventsFromPlanYears, requirementGroupKey } from './helpers'
import BcsTooltip from '../BcsTooltip.vue'
import BcsStatusPill from '../common/BcsStatusPill.vue'
import BcsDynamicText from '../BcsDynamicText.vue'
import { BOOK_VIEWS } from './constants'
import barChartIcon from '../../../assets/images/bar-chart-icon.svg'
import reminderAlertIcon from '../../../assets/images/reminder-alert-icon.svg'
import calendarClockIcon from '../../../assets/images/calendar-clock-icon.svg'

const actionCenterStore = useActionCenterStore()
const navigationStore = useNavigationStore()
const templateStore = useTemplateVariablesStore()
const { getDefinitionBodySync } = templateStore
const emitter = inject('emitter', null)

const { customNavigation } = storeToRefs(navigationStore)
const { filteredPlanYears, accountOwnerPlanYears, historicalReminders, isLoadingHistoricalReminders } = storeToRefs(actionCenterStore)

const scrollToSection = (sectionId) => {
  setTimeout(() => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, 100)
}

const openBookDetailsView = (view) => {
  actionCenterStore.setBookDetailsView(view)
  scrollToSection('bookTableManagerSection')
}


// ===== BASE STATE FLAGS =====
const hasAccountOwnerPlanYears = computed(() => accountOwnerPlanYears.value.length > 0)
const hasFilteredEmployers = computed(() => filteredPlanYears.value.length > 0)

const filteredResultsText = computed(() => {
  const filtered = filteredPlanYears.value.length
  const total = accountOwnerPlanYears.value.length
  return `Based on filtered results: ${filtered} of ${total} employers`
})

// ===== RAW DATA: Card 1 — Employers Require Action =====
const riskData = computed(() => {
  const completedAssessments = filteredPlanYears.value.filter(py => py.assessment_status === 4)

  const employersNeedingAction = filteredPlanYears.value.filter(py =>
    py.assessment_status === 4 && Array.isArray(py.action_items) && py.action_items.length > 0
  ).length

  let avgRiskScore = 0
  if (completedAssessments.length > 0) {
    const totalPercent = completedAssessments.reduce((sum, py) => {
      return sum + (parseFloat(py.percentage_compliance_risk) || 0)
    }, 0)
    avgRiskScore = Math.round(totalPercent / completedAssessments.length)
  }

  return { employersNeedingAction, avgRiskScore }
})

// ===== RAW DATA: Card 2 — Upcoming Calendar Requirements =====
const remindersData = computed(() => {
  const activeEvents = extractActiveEventsFromPlanYears(filteredPlanYears.value)
  const totalDeadlines = activeEvents.length

  if (totalDeadlines === 0) {
    return { upcomingCount: 0, percentageSent: 0 }
  }

  const reminders = historicalReminders.value || []
  let remindersSent = 0

  const now = new Date()
  const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)
  let upcomingCount = 0

  for (const ev of activeEvents) {
    const reqKey = requirementGroupKey(ev.eventBody, ev.eventOriginalDateMD)
    const hasReminder = reminders.some(r =>
      r.employer_id === ev.employerId && r.requirement_key === reqKey
    )
    if (hasReminder) remindersSent++

    if (ev.dueDateObj >= now && ev.dueDateObj <= thirtyDaysFromNow) {
      upcomingCount++
    }
  }

  const percentageSent = Math.round((remindersSent / totalDeadlines) * 100)

  return { upcomingCount, percentageSent }
})

// ===== RAW DATA: Card 3 — Incomplete Assessments =====
const assessmentsData = computed(() => {
  const total = filteredPlanYears.value.length
  if (total === 0) return { incomplete: 0, percentage: 0 }

  const completed = filteredPlanYears.value.filter(py => py.assessment_status === 4).length
  const incomplete = total - completed
  const percentage = Math.round((completed / total) * 100)

  return { incomplete, percentage }
})

// ===== CARD STATE CONFIGURATION =====
// States: 'no-data' | 'no-results' | 'needs-action' | 'complete'

const riskCard = computed(() => {
  let state = 'no-data'
  if (hasAccountOwnerPlanYears.value) {
    if (!hasFilteredEmployers.value) {
      state = 'no-results'
    } else {
      state = riskData.value.employersNeedingAction > 0 ? 'needs-action' : 'complete'
    }
  }

  const configs = {
    'no-data': {
      showBadge: false,
      count: 0,
      descriptionKey: 'bookQuickActionsRiskDescriptionBase',
      buttonText: 'Add Employers to Start',
      buttonAction: 'add-employers',
      buttonDisabled: false,
      buttonClass: 'btn-primary'
    },
    'no-results': {
      showBadge: false,
      count: 0,
      descriptionKey: 'bookQuickActionsRiskDescriptionBase',
      buttonText: 'View Action Items',
      buttonAction: null,
      buttonDisabled: true,
      buttonClass: 'btn-primary'
    },
    'needs-action': {
      showBadge: true,
      count: riskData.value.employersNeedingAction,
      descriptionKey: 'bookQuickActionsRiskDescriptionBase',
      buttonText: 'View Action Items',
      buttonAction: 'view-action-items',
      buttonDisabled: false,
      buttonClass: 'btn-primary'
    },
    'complete': {
      showBadge: true,
      count: 0,
      descriptionKey: 'bookQuickActionsRiskCompleteDescription',
      buttonText: 'View Action Items',
      buttonAction: 'view-action-items',
      buttonDisabled: false,
      buttonClass: 'btn-primary'
    }
  }

  return { state, ...configs[state] }
})

const remindersCard = computed(() => {
  let state = 'no-data'
  if (hasAccountOwnerPlanYears.value) {
    if (!hasFilteredEmployers.value) {
      state = 'no-results'
    } else {
      state = remindersData.value.upcomingCount > 0 ? 'needs-action' : 'complete'
    }
  }

  const configs = {
    'no-data': {
      showBadge: false,
      count: 0,
      descriptionKey: 'bookQuickActionsRemindersDescriptionBase',
      buttonText: 'Add Employers to Start',
      buttonAction: 'add-employers',
      buttonDisabled: false,
      buttonClass: 'btn-primary'
    },
    'no-results': {
      showBadge: false,
      count: 0,
      descriptionKey: 'bookQuickActionsRemindersDescriptionBase',
      buttonText: 'Send Calendar Reminders',
      buttonAction: null,
      buttonDisabled: true,
      buttonClass: 'btn-primary'
    },
    'needs-action': {
      showBadge: true,
      count: remindersData.value.upcomingCount,
      descriptionKey: 'bookQuickActionsRemindersDescriptionBase',
      buttonText: 'Send Calendar Reminders',
      buttonAction: 'send-reminders',
      buttonDisabled: false,
      buttonClass: 'btn-primary'
    },
    'complete': {
      showBadge: true,
      count: 0,
      descriptionKey: 'bookQuickActionsRemindersCompleteDescription',
      buttonText: 'View Sent Reminders',
      buttonAction: 'view-reminders',
      buttonDisabled: false,
      buttonClass: 'btn-primary'
    }
  }

  return { state, ...configs[state] }
})

const assessmentsCard = computed(() => {
  let state = 'no-data'
  if (hasAccountOwnerPlanYears.value) {
    if (!hasFilteredEmployers.value) {
      state = 'no-results'
    } else {
      state = assessmentsData.value.incomplete > 0 ? 'needs-action' : 'complete'
    }
  }

  const configs = {
    'no-data': {
      showBadge: false,
      count: 0,
      descriptionKey: 'bookQuickActionsAssessmentsDescriptionBase',
      buttonText: 'Add Employers to Start',
      buttonAction: 'add-employers',
      buttonDisabled: false,
      buttonClass: 'btn-primary'
    },
    'no-results': {
      showBadge: false,
      count: 0,
      descriptionKey: 'bookQuickActionsAssessmentsDescriptionBase',
      buttonText: 'Complete Assessments',
      buttonAction: null,
      buttonDisabled: true,
      buttonClass: 'btn-primary'
    },
    'needs-action': {
      showBadge: true,
      count: assessmentsData.value.incomplete,
      descriptionKey: 'bookQuickActionsAssessmentsNeedsActionDescription',
      buttonText: 'Complete Assessments',
      buttonAction: 'complete-assessments',
      buttonDisabled: false,
      buttonClass: 'btn-primary'
    },
    'complete': {
      showBadge: true,
      count: 0,
      descriptionKey: 'bookQuickActionsAssessmentsCompleteDescription',
      buttonText: 'View Assessments',
      buttonAction: 'view-assessments',
      buttonDisabled: false,
      buttonClass: 'btn-primary'
    }
  }

  return { state, ...configs[state] }
})

// ===== BADGE COLOR =====
const getBadgeColor = (percentage) => {
  if (percentage >= 75) return 'success'
  if (percentage >= 50) return 'warning'
  return 'danger'
}

// ===== BUTTON ACTION HANDLER =====
const handleCardAction = (action) => {
  if (!action) return

  const actions = {
    'add-employers': () => {
      customNavigation.value.navigate({
        newPage: customNavigation.value.routes.ListOfEmployers.name
      })
    },
    'view-action-items': () => {
      openBookDetailsView(BOOK_VIEWS.INCOMPLETE_ACTIONS)
    },
    'send-reminders': () => {
      openBookDetailsView(BOOK_VIEWS.UPCOMING_REQUIREMENTS)
    },
    'view-reminders': () => {
      openBookDetailsView(BOOK_VIEWS.REQUIREMENTS_BY_TYPE)
    },
    'complete-assessments': () => {
      openBookDetailsView(BOOK_VIEWS.INCOMPLETE_ASSESSMENTS)
    },
    'view-assessments': () => {
      openBookDetailsView(BOOK_VIEWS.ALL)
    }
  }

  actions[action]?.()
}
</script>

<template>
  <div class="book-quick-actions-container bg-info">
    <div class="book-quick-actions-content bcs-page-padding">
      <!-- Section Header -->
      <div class="d-flex align-items-center gap-2 mb-4">
        <h2 class="header-20 mb-0"><strong>Book Quick Actions</strong></h2>
        <span id="filteredResultsQuickActions" class="text-14 text-gray-600">
          {{ filteredResultsText }}
        </span>
        <BcsTooltip
          :simpleText="'Use the filters at the top to see more employers or change the list.'"
          :show-arrow="true"
          :contentClass="'simple-text-tooltip'"
        >
          <i class="bi bi-info-circle text-primary" />
        </BcsTooltip>
      </div>

      <!-- Cards Container -->
      <div class="quick-actions-grid">

        <!-- Card 1: Employers Require Action -->
        <div class="quick-action-card">
          <div v-if="riskCard.showBadge" class="card-badge-container">
            <BcsStatusPill
              :text="`${riskData.avgRiskScore}% Total Average Risk Score`"
              :color="getBadgeColor(riskData.avgRiskScore)"
              :tooltip-text="getDefinitionBodySync('bookQuickActionsRiskBadgeTooltip')"
              content-class="text-16"
            />
          </div>
          <div class="card-body-content" :class="{ 'pt-badge-absent': !riskCard.showBadge }">
            <div class="card-icon">
              <img :src="barChartIcon" alt="Bar chart" width="42" height="42" />
            </div>
            <p class="card-title header-20">
              <strong>{{ riskCard.count }} Employers Require Action</strong>
            </p>
            <p class="card-description text-14 text-gray-600">
              <BcsDynamicText :text="riskCard.descriptionKey" />
            </p>
          </div>
          <div class="card-action">
            <button
              id="quickActionViewItems"
              class="btn btn-quick-action"
              :class="riskCard.buttonClass"
              :disabled="riskCard.buttonDisabled"
              @click="handleCardAction(riskCard.buttonAction)"
            >
              <med>{{ riskCard.buttonText }} <i class="bi bi-chevron-right ms-1"></i></med>
            </button>
          </div>
        </div>

        <!-- Card 2: Upcoming Calendar Requirements -->
        <div class="quick-action-card">
          <div v-if="remindersCard.showBadge" class="card-badge-container">
            <BcsStatusPill
              :text="isLoadingHistoricalReminders ? 'Loading reminders...' : `${remindersData.percentageSent}% Total Calendar Reminders Sent`"
              :color="isLoadingHistoricalReminders ? 'neutral' : getBadgeColor(remindersData.percentageSent)"
              :tooltip-text="getDefinitionBodySync('bookQuickActionsRemindersBadgeTooltip')"
              content-class="text-16"
            />
          </div>
          <div class="card-body-content" :class="{ 'pt-badge-absent': !remindersCard.showBadge }">
            <div class="card-icon">
              <img :src="reminderAlertIcon" alt="Reminder alert" width="42" height="42" />
            </div>
            <p class="card-title header-20">
              <strong>{{ remindersCard.count }} Upcoming Calendar Requirements</strong>
            </p>
            <p class="card-description text-14 text-gray-600">
              <BcsDynamicText :text="remindersCard.descriptionKey" />
            </p>
          </div>
          <div class="card-action">
            <button
              id="quickActionSendReminders"
              class="btn btn-quick-action"
              :class="remindersCard.buttonClass"
              :disabled="remindersCard.buttonDisabled"
              @click="handleCardAction(remindersCard.buttonAction)"
            >
              <med>{{ remindersCard.buttonText }} <i class="bi bi-chevron-right ms-1"></i></med>
            </button>
          </div>
        </div>

        <!-- Card 3: Incomplete Assessments -->
        <div class="quick-action-card">
          <div v-if="assessmentsCard.showBadge" class="card-badge-container">
            <BcsStatusPill
              :text="`${assessmentsData.percentage}% Total Assessments Complete`"
              :color="getBadgeColor(assessmentsData.percentage)"
              :tooltip-text="getDefinitionBodySync('bookQuickActionsAssessmentsBadgeTooltip')"
              content-class="text-16"
            />
          </div>
          <div class="card-body-content" :class="{ 'pt-badge-absent': !assessmentsCard.showBadge }">
            <div class="card-icon">
              <img :src="calendarClockIcon" alt="Calendar clock" width="48" height="48" />
            </div>
            <p class="card-title header-20">
              <strong>{{ assessmentsCard.count }} Incomplete Assessments</strong>
            </p>
            <p class="card-description text-14 text-gray-600">
              <BcsDynamicText :text="assessmentsCard.descriptionKey" />
            </p>
          </div>
          <div class="card-action">
            <button
              id="quickActionCompleteAssessments"
              class="btn btn-quick-action"
              :class="assessmentsCard.buttonClass"
              :disabled="assessmentsCard.buttonDisabled"
              @click="handleCardAction(assessmentsCard.buttonAction)"
            >
              <med>{{ assessmentsCard.buttonText }} <i class="bi bi-chevron-right ms-1"></i></med>
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.book-quick-actions-container {
  padding: 1.8rem 0 2rem;
}

// Grid Layout
.quick-actions-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}

// Individual Card
.quick-action-card {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 12px;
  padding: 0 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
}

// Badge (positioned to overlap top border)
.card-badge-container {
  display: flex;
  justify-content: center;
  margin-top: -12px;
  margin-bottom: 1rem;
}

// Card Body
.card-body-content {
  flex: 1;
  text-align: center;
  padding: 0 0.5rem;
}

// Extra top padding when badge is hidden
.pt-badge-absent {
  padding-top: 1.5rem;
}

.card-icon {
  font-size: 1.75rem;
  color: #495057;
  margin-bottom: 0.75rem;
}

.card-title {
  margin-bottom: 0.5rem;
}

.card-description {
  margin-bottom: 1rem;
  line-height: 1.4;
}

// Action Buttons
.card-action {
  margin-top: auto;
}

.btn-quick-action {
  width: 100%;
  padding: 0.6rem 1rem;
  border-radius: 6px;
  font-size: 0.9375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  transition: background-color 0.15s ease, transform 0.1s ease;
  border: none;

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  i {
    font-size: 0.8125rem;
  }
}

</style>
